import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Button, Typography, Tag, Badge, Empty, Spin, message, Modal,
  Tooltip, Checkbox, Space, Progress, Row, Col, Statistic, Input, Divider,
  Select,
} from 'antd';
import {
  QrcodeOutlined, CameraOutlined, FileImageOutlined, FilePdfOutlined,
  DeleteOutlined, ReloadOutlined, CheckCircleFilled, CloseCircleFilled,
  PlusOutlined, ThunderboltOutlined, ScanOutlined, ClockCircleOutlined,
  FolderOutlined, MobileOutlined, DisconnectOutlined, LinkOutlined,
  ArrowLeftOutlined, SelectOutlined, FileAddOutlined, RobotOutlined,
  CheckSquareOutlined, CloseOutlined, EyeOutlined, LoadingOutlined,
  CloudUploadOutlined, TagOutlined, ShopOutlined, WarningOutlined,
  FireOutlined, SearchOutlined, CarOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://pts-pettycash-api.onrender.com';
const POLL_INTERVAL = 3000;

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const ScanSession = ({ folderPath, folderNumber, moduleType, folderName, username, onClose, onProcessComplete }) => {
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [mobileUrl, setMobileUrl] = useState('');
  const [sessionActive, setSessionActive] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [creating, setCreating] = useState(false);

  // ── FUEL MODE ──
  const [isFuelMode, setIsFuelMode] = useState(false);
  const [fuelNames, setFuelNames] = useState([]);
  const [fuelAssignments, setFuelAssignments] = useState({}); // { photo_id: { serial_no, fuel_name } }
  const [addNewFuelOpen, setAddNewFuelOpen] = useState(false);
  const [newFuelName, setNewFuelName] = useState('');
  const [addingFuel, setAddingFuel] = useState(false);
  const [nextSerial, setNextSerial] = useState(56);

  // Photos state
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  // PDF groups state
  const [pdfGroups, setPdfGroups] = useState([]);

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [processResults, setProcessResults] = useState(null);
  const [stage, setStage] = useState('setup');

  // QR Code
  const [qrImageUrl, setQrImageUrl] = useState('');
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  // ─── Fetch fuel names ───
  const fetchFuelNames = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fuel-names`);
      if (response.data.success) {
        setFuelNames(response.data.fuel_names || []);
      }
    } catch { /* ignore */ }
  }, []);

  const fetchNextSerial = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fuel-names/next-serial`);
      if (response.data.success) setNextSerial(response.data.next_serial);
    } catch { /* ignore */ }
  };

  // ─── Create Session ───
  const createSession = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/scan-session`, {
        folder_path: folderPath,
        folder_number: folderNumber,
        module_type: moduleType,
        username: username || '',
        is_fuel_mode: isFuelMode,
      });

      if (response.data.success) {
        const { session_id, mobile_url, timeout_seconds } = response.data;
        setSessionId(session_id);
        setMobileUrl(mobile_url);
        setSessionActive(true);
        setRemainingTime(timeout_seconds);
        setStage('scanning');

        const fullUrl = `https://pettycash.phoenician-uae.org/scan/${session_id}`;
        setQrImageUrl(`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(fullUrl)}`);
        setMobileUrl(fullUrl);

        if (isFuelMode) fetchFuelNames();
        message.success('Scan session created! Scan the QR code with your phone.');
      } else {
        message.error(response.data.message || 'Failed to create session');
      }
    } catch (err) {
      message.error('Failed to create scan session');
    } finally {
      setCreating(false);
    }
  };

  // ─── Poll for new photos ───
  const pollSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/scan-session/${sessionId}`);
      if (response.data.success) {
        const newPhotos = response.data.photos || [];
        setPhotos(newPhotos);
        setRemainingTime(response.data.remaining_seconds || 0);

        // Update fuel assignments from session data (mobile sends them)
        if (response.data.fuel_assignments) {
          setFuelAssignments(prev => ({ ...prev, ...response.data.fuel_assignments }));
        }

        if (response.data.remaining_seconds <= 0) {
          setSessionActive(false);
          message.warning('Scan session has expired');
        }
      }
    } catch (err) {
      if (err.response?.status === 404) setSessionActive(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionActive && sessionId && stage === 'scanning') {
      pollSession();
      pollRef.current = setInterval(pollSession, POLL_INTERVAL);
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 0) { setSessionActive(false); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionActive, sessionId, stage, pollSession]);

  // ─── Delete photo ───
  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/scan-session/${sessionId}/photo/${photoId}`);
      setPhotos(prev => prev.filter(p => p.photo_id !== photoId));
      setSelectedPhotos(prev => { const next = new Set(prev); next.delete(photoId); return next; });
    } catch { message.error('Failed to remove photo'); }
  };

  const togglePhoto = (photoId) => {
    setSelectedPhotos(prev => {
      const next = new Set(prev);
      next.has(photoId) ? next.delete(photoId) : next.add(photoId);
      return next;
    });
  };

  const selectAll = () => {
    const groupedIds = new Set(pdfGroups.flatMap(g => g.photo_ids));
    const available = photos.filter(p => !groupedIds.has(p.photo_id));
    if (selectedPhotos.size === available.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(available.map(p => p.photo_id)));
    }
  };

  const createGroup = () => {
    if (selectedPhotos.size === 0) { message.warning('Select photos first'); return; }
    const groupId = Date.now().toString();
    const photoIds = Array.from(selectedPhotos);

    // For fuel mode, try to get the fuel assignment from the first photo
    let fuelInfo = null;
    if (isFuelMode) {
      for (const pid of photoIds) {
        if (fuelAssignments[pid]) { fuelInfo = fuelAssignments[pid]; break; }
      }
    }

    setPdfGroups(prev => [...prev, {
      id: groupId,
      photo_ids: photoIds,
      label: `Document ${prev.length + 1}`,
      fuel_serial: fuelInfo?.serial_no || null,
      fuel_name: fuelInfo?.fuel_name || null,
    }]);
    setSelectedPhotos(new Set());
    message.success(`Created PDF group with ${photoIds.length} page(s)`);
  };

  const removeGroup = (groupId) => {
    setPdfGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const createIndividualGroups = () => {
    const groupedIds = new Set(pdfGroups.flatMap(g => g.photo_ids));
    const ungrouped = photos.filter(p => !groupedIds.has(p.photo_id));
    if (ungrouped.length === 0) { message.info('All photos are already grouped'); return; }

    const newGroups = ungrouped.map((photo, i) => {
      const fuelInfo = fuelAssignments[photo.photo_id];
      return {
        id: Date.now().toString() + i,
        photo_ids: [photo.photo_id],
        label: `Document ${pdfGroups.length + i + 1}`,
        fuel_serial: fuelInfo?.serial_no || null,
        fuel_name: fuelInfo?.fuel_name || null,
      };
    });

    setPdfGroups(prev => [...prev, ...newGroups]);
    message.success(`Created ${newGroups.length} individual PDF(s)`);
  };

  const goToGrouping = () => {
    if (photos.length === 0) { message.warning('No photos to process.'); return; }
    setStage('grouping');
    setSelectMode(true);
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (isFuelMode) { fetchFuelNames(); fetchNextSerial(); }
  };

  // ─── Handle fuel assignment for a group (desktop side) ───
  // When assigning fuel to a group
const assignFuelToGroup = (groupId, serialNo) => {
  const fuel = fuelNames.find(f => f.serial_no === serialNo);  // ← serial_no, not serial_number
  if (!fuel) return;
  setPdfGroups(prev => prev.map(g =>
    g.id === groupId ? { 
      ...g, 
      fuel_serial: fuel.serial_no,  // ← This matches
      fuel_name: fuel.fuel_name      // ← This matches
    } : g
  ));
};

  // ─── Add new fuel name ───
  const handleAddNewFuel = async () => {
    if (!newFuelName.trim()) { message.warning('Enter a fuel name'); return; }
    setAddingFuel(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/fuel-names`, {
        fuel_name: newFuelName.trim(),
        created_by: username || 'desktop',
      });
      if (response.data.success) {
        message.success(`Created #${response.data.serial_no}: ${response.data.fuel_name}`);
        setNewFuelName('');
        setAddNewFuelOpen(false);
        fetchFuelNames();
        fetchNextSerial();
      } else {
        message.error(response.data.message);
      }
    } catch { message.error('Failed to create'); }
    finally { setAddingFuel(false); }
  };

  // ─── Process all groups ───
  const processAllGroups = async () => {
    if (pdfGroups.length === 0) { message.warning('Create at least one PDF group first'); return; }

    // In fuel mode, check all groups have fuel assignments
    if (isFuelMode) {
      const unassigned = pdfGroups.filter(g => !g.fuel_serial);
      if (unassigned.length > 0) {
        message.warning(`${unassigned.length} group(s) need a fuel name assigned. Please assign before processing.`);
        return;
      }
    }

    setStage('processing');
    setProcessing(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/scan-session/${sessionId}/process`,
        {
          groups: pdfGroups.map(g => ({
            photo_ids: g.photo_ids,
            label: g.label,
            fuel_serial: g.fuel_serial,
            fuel_name: g.fuel_name,
          })),
          is_fuel_mode: isFuelMode,
        },
        { timeout: 300000 }
      );

      setProcessResults(response.data);
      setStage('done');

      if (response.data.success) {
        message.success(`Processed ${response.data.processed} documents!`);
      } else {
        message.warning(`Completed with ${response.data.errors} error(s)`);
      }
      if (onProcessComplete) onProcessComplete(response.data);
    } catch (err) {
      message.error('Processing failed: ' + (err.message || 'Unknown error'));
      setStage('grouping');
    } finally {
      setProcessing(false);
    }
  };

  const endSession = async () => {
    if (sessionId) { try { await axios.delete(`${API_BASE_URL}/api/scan-session/${sessionId}`); } catch { /* ignore */ } }
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    setSessionActive(false);
    if (onClose) onClose();
  };

  const groupedIds = new Set(pdfGroups.flatMap(g => g.photo_ids));
  const ungroupedPhotos = photos.filter(p => !groupedIds.has(p.photo_id));

  // ═══════════════════════════════════════════
  //  RENDER: SETUP — with Fuel Mode checkbox
  // ═══════════════════════════════════════════

  if (stage === 'setup') {
    return (
      <Card style={{ borderRadius: 12, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
        styles={{ body: { padding: '32px' } }}>
        <div style={{ textAlign: 'center', maxWidth: 500, margin: '0 auto' }}>
          <div style={{ width: 72, height: 72, borderRadius: 18, margin: '0 auto 20px', background: 'linear-gradient(135deg, #1a6ed8, #4a9eff)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(26,110,216,0.3)' }}>
            <MobileOutlined style={{ fontSize: 32, color: '#fff' }} />
          </div>

          <Title level={4} style={{ margin: '0 0 8px', color: '#1a3353' }}>
            Scan Invoices with Your Phone
          </Title>
          <Text style={{ color: '#5a6d80', fontSize: 14, display: 'block', marginBottom: 24 }}>
            A QR code will appear on screen. Scan it with your phone to upload invoice photos.
          </Text>

          <div style={{ background: '#f0f7ff', border: '1px solid #d6e4f0', borderRadius: 10, padding: '14px 18px', marginBottom: 20, textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FolderOutlined style={{ color: '#1a6ed8' }} />
              <Text strong style={{ color: '#1a3353' }}>Folder {folderNumber}</Text>
              <Tag color={moduleType === 'site-petty-cash' ? 'processing' : 'warning'} style={{ borderRadius: 4, fontSize: 10 }}>
                {moduleType === 'site-petty-cash' ? 'Site Petty Cash' : 'Procurement PC'}
              </Tag>
            </div>
          </div>

          {/* ═══ FUEL MODE CHECKBOX ═══ */}
          <div
            onClick={() => setIsFuelMode(!isFuelMode)}
            style={{
              background: isFuelMode
                ? 'linear-gradient(135deg, #fff2e8, #ffe7ba)'
                : '#fafbfd',
              border: isFuelMode
                ? '2px solid #fa541c'
                : '1px solid #e8ecf1',
              borderRadius: 12,
              padding: '16px 18px',
              marginBottom: 24,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: isFuelMode
                ? 'linear-gradient(135deg, #fa541c, #ff7a45)'
                : '#e8ecf1',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}>
              <FireOutlined style={{ fontSize: 22, color: isFuelMode ? '#fff' : '#8dabc4' }} />
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <Text strong style={{ fontSize: 15, color: '#1a3353', display: 'block' }}>
                Fuel Invoice Mode
              </Text>
              <Text style={{ fontSize: 12, color: '#5a6d80' }}>
                Enable to assign fuel names (driver/vehicle) to each scanned invoice.
                Files will be named with serial numbers.
              </Text>
            </div>
            <Checkbox
              checked={isFuelMode}
              onChange={(e) => { e.stopPropagation(); setIsFuelMode(e.target.checked); }}
              style={{ transform: 'scale(1.3)' }}
            />
          </div>

          <Button
            type="primary" size="large" icon={<QrcodeOutlined />}
            onClick={createSession} loading={creating}
            style={{
              height: 52, borderRadius: 12, fontWeight: 600, fontSize: 15, width: '100%',
              background: isFuelMode
                ? 'linear-gradient(135deg, #fa541c, #ff7a45)'
                : 'linear-gradient(135deg, #1a6ed8, #4a9eff)',
              border: 'none',
              boxShadow: isFuelMode
                ? '0 4px 16px rgba(250,84,28,0.3)'
                : '0 4px 16px rgba(26,110,216,0.3)',
            }}
          >
            {isFuelMode ? 'Start Fuel Scan Session' : 'Start Scan Session'}
          </Button>
        </div>
      </Card>
    );
  }

  // ═══════════════════════════════════════════
  //  RENDER: SCANNING
  // ═══════════════════════════════════════════

  if (stage === 'scanning') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={endSession} style={{ borderRadius: 8 }}>Close</Button>
            <div>
              <Title level={5} style={{ margin: 0, color: '#1a3353' }}>
                <ScanOutlined style={{ color: '#1a6ed8', marginRight: 8 }} />
                Scanning — Folder {folderNumber}
                {isFuelMode && (
                  <Tag color="volcano" style={{ marginLeft: 8, borderRadius: 4, fontSize: 10, verticalAlign: 'middle' }}>
                    <FireOutlined /> FUEL MODE
                  </Tag>
                )}
              </Title>
              <Text style={{ fontSize: 12, color: '#8dabc4' }}>
                Scan QR with phone · Photos appear here automatically
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Tag icon={<ClockCircleOutlined />} color={remainingTime < 300 ? 'error' : 'processing'}>
              {formatTime(remainingTime)}
            </Tag>
            <Badge count={photos.length} style={{ backgroundColor: '#1a6ed8' }}>
              <Tag icon={<CameraOutlined />} color="blue">{photos.length} photos</Tag>
            </Badge>
          </div>
        </div>

        <Row gutter={24}>
          <Col xs={24} md={8}>
            <Card style={{ borderRadius: 12, border: '1px solid #d6e4f0', textAlign: 'center' }} styles={{ body: { padding: '24px 16px' } }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, margin: '0 auto 16px', background: 'linear-gradient(135deg, #e8f1ff, #d6e4f0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <QrcodeOutlined style={{ fontSize: 26, color: '#1a6ed8' }} />
              </div>
              <Text strong style={{ fontSize: 14, color: '#1a3353', display: 'block', marginBottom: 12 }}>Scan with Phone</Text>
              {qrImageUrl ? (
                <div style={{ background: '#fff', padding: 12, borderRadius: 12, border: '2px solid #e8ecf1', display: 'inline-block', marginBottom: 16 }}>
                  <img src={qrImageUrl} alt="QR Code" style={{ width: 200, height: 200 }} />
                </div>
              ) : <Spin size="large" style={{ margin: '20px 0' }} />}
              <Text style={{ fontSize: 11, color: '#8dabc4', display: 'block', marginBottom: 12 }}>Or open this link on your phone:</Text>
              <Input value={mobileUrl} readOnly size="small" style={{ borderRadius: 6, fontSize: 11, marginBottom: 12 }}
                addonAfter={<Tooltip title="Copy link"><LinkOutlined onClick={() => { navigator.clipboard.writeText(mobileUrl); message.success('Copied!'); }} style={{ cursor: 'pointer' }} /></Tooltip>} />

              {isFuelMode && (
                <div style={{ background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 8, padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#ad2102', marginTop: 8 }}>
                  <strong><FireOutlined /> Fuel Mode Active</strong>
                  <div style={{ marginTop: 4, color: '#d46b08' }}>
                    After uploading each photo on the phone, the user will select a fuel name from the list.
                  </div>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} md={16}>
            <Card
              title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FileImageOutlined style={{ color: '#faad14', fontSize: 16 }} /><span style={{ fontWeight: 600, color: '#1a3353' }}>Scanned Photos</span><Badge count={photos.length} style={{ backgroundColor: '#52c41a', marginLeft: 4 }} size="small" /></div>}
              extra={<Button icon={<ReloadOutlined />} size="small" onClick={pollSession}>Refresh</Button>}
              style={{ borderRadius: 12, border: '1px solid #e8ecf1' }}
              styles={{ body: { padding: '16px' } }}
            >
              {photos.length === 0 ? (
                <div style={{ padding: '50px 20px', textAlign: 'center' }}>
                  <div style={{ width: 64, height: 64, borderRadius: 16, margin: '0 auto 16px', background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CameraOutlined style={{ fontSize: 28, color: '#1a6ed8' }} />
                  </div>
                  <Text style={{ color: '#5a6d80', fontSize: 14 }}>Waiting for photos...</Text>
                  <br />
                  <Text style={{ color: '#8dabc4', fontSize: 12 }}>Scan the QR code and start taking pictures</Text>
                  <div style={{ marginTop: 16 }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 20, color: '#1a6ed8' }} spin />} />
                    <Text style={{ marginLeft: 10, fontSize: 12, color: '#8dabc4' }}>Listening for uploads...</Text>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
                  {photos.map((photo, i) => {
                    const fuelInfo = fuelAssignments[photo.photo_id];
                    return (
                      <div key={photo.photo_id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid #e8ecf1', background: '#fafbfd' }}>
                        <div style={{ position: 'absolute', top: 6, left: 6, zIndex: 2 }}>
                          <Tag style={{ borderRadius: 4, fontSize: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none' }}>#{i + 1}</Tag>
                        </div>
                        <Tooltip title="Remove photo">
                          <Button type="text" size="small" danger icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                            style={{ position: 'absolute', top: 4, right: 4, zIndex: 2, width: 24, height: 24, background: 'rgba(255,255,255,0.9)', borderRadius: 6 }}
                            onClick={() => deletePhoto(photo.photo_id)} />
                        </Tooltip>
                        <img src={`${API_BASE_URL}${photo.preview_url}`} alt={`Scan ${i + 1}`} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
                        <div style={{ padding: '6px 8px' }}>
                          <Text style={{ fontSize: 10, color: '#8dabc4' }}>{formatFileSize(photo.size)}</Text>
                          {isFuelMode && fuelInfo && (
                            <Tag color="volcano" style={{ borderRadius: 3, fontSize: 9, margin: '2px 0 0', display: 'block' }}>
                              #{fuelInfo.serial_no} {fuelInfo.fuel_name?.substring(0, 20)}
                            </Tag>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {photos.length > 0 && (
              <Button type="primary" size="large" icon={<ThunderboltOutlined />} onClick={goToGrouping}
                style={{
                  marginTop: 16, width: '100%', height: 52, borderRadius: 12, fontWeight: 600, fontSize: 15,
                  background: isFuelMode ? 'linear-gradient(135deg, #fa541c, #ff7a45)' : 'linear-gradient(135deg, #722ed1, #9254de)',
                  border: 'none',
                }}>
                Next: Create PDFs & Process ({photos.length} photos)
              </Button>
            )}
          </Col>
        </Row>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  //  RENDER: GROUPING — with fuel name assignment on each group
  // ═══════════════════════════════════════════

  if (stage === 'grouping') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => { setStage('scanning'); setSelectMode(false); setSelectedPhotos(new Set()); }} style={{ borderRadius: 8 }}>Back</Button>
            <Title level={5} style={{ margin: 0, color: '#1a3353' }}>
              <FilePdfOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
              Create PDF Documents
              {isFuelMode && <Tag color="volcano" style={{ marginLeft: 8, borderRadius: 4, fontSize: 10, verticalAlign: 'middle' }}><FireOutlined /> FUEL</Tag>}
            </Title>
          </div>
        </div>

        {isFuelMode && (
          <div style={{ background: 'linear-gradient(135deg, #fff2e8, #ffe7ba)', border: '1px solid #ffbb96', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
            <Text strong style={{ color: '#ad2102', fontSize: 13, display: 'block', marginBottom: 4 }}>
              <FireOutlined style={{ marginRight: 6 }} /> Fuel Mode — Assign a fuel name to each document
            </Text>
            <Text style={{ color: '#d46b08', fontSize: 12 }}>
              After grouping photos into PDFs, select the matching fuel name (driver/vehicle) for each group.
              The serial number will be added to the filename.
            </Text>
          </div>
        )}

        <Row gutter={20}>
          <Col xs={24} md={14}>
            <Card
              title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FileImageOutlined style={{ color: '#faad14' }} /><span style={{ fontWeight: 600, color: '#1a3353' }}>Available Photos</span><Badge count={ungroupedPhotos.length} style={{ backgroundColor: '#faad14' }} size="small" /></div>}
              extra={
                <Space>
                  <Button size="small" onClick={selectAll} style={{ borderRadius: 6 }}>
                    {selectedPhotos.size === ungroupedPhotos.length && ungroupedPhotos.length > 0 ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button type="primary" size="small" icon={<PlusOutlined />} disabled={selectedPhotos.size === 0} onClick={createGroup} style={{ borderRadius: 6 }}>
                    Create PDF Group ({selectedPhotos.size})
                  </Button>
                </Space>
              }
              style={{ borderRadius: 12, border: '1px solid #e8ecf1', marginBottom: 16 }}
              styles={{ body: { padding: 12 } }}
            >
              {ungroupedPhotos.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center' }}><CheckCircleFilled style={{ fontSize: 32, color: '#52c41a', marginBottom: 8 }} /><Text style={{ display: 'block', color: '#52c41a', fontWeight: 600 }}>All photos grouped!</Text></div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
                  {ungroupedPhotos.map((photo) => {
                    const isSelected = selectedPhotos.has(photo.photo_id);
                    return (
                      <div key={photo.photo_id} onClick={() => togglePhoto(photo.photo_id)}
                        style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: isSelected ? '3px solid #1a6ed8' : '1px solid #e8ecf1', background: isSelected ? '#e8f1ff' : '#fafbfd', transition: 'all 0.15s', transform: isSelected ? 'scale(0.97)' : 'scale(1)' }}>
                        {isSelected && (
                          <div style={{ position: 'absolute', top: 6, right: 6, zIndex: 2, width: 24, height: 24, borderRadius: 12, background: '#1a6ed8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckCircleFilled style={{ color: '#fff', fontSize: 14 }} />
                          </div>
                        )}
                        <div style={{ position: 'absolute', top: 6, left: 6, zIndex: 2 }}>
                          <Tag style={{ borderRadius: 4, fontSize: 9, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none' }}>#{photos.indexOf(photo) + 1}</Tag>
                        </div>
                        <img src={`${API_BASE_URL}${photo.preview_url}`} alt="Scan" style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
                      </div>
                    );
                  })}
                </div>
              )}
              {ungroupedPhotos.length > 0 && (
                <Button icon={<FileAddOutlined />} block onClick={createIndividualGroups} style={{ marginTop: 12, borderRadius: 8, borderStyle: 'dashed' }}>
                  Make Each Remaining Photo a Separate PDF ({ungroupedPhotos.length})
                </Button>
              )}
            </Card>
          </Col>

          <Col xs={24} md={10}>
            <Card
              title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FilePdfOutlined style={{ color: '#ff4d4f' }} /><span style={{ fontWeight: 600, color: '#1a3353' }}>PDF Documents</span><Badge count={pdfGroups.length} style={{ backgroundColor: '#ff4d4f' }} size="small" /></div>}
              extra={isFuelMode && (
                <Button size="small" icon={<PlusOutlined />} onClick={() => { setAddNewFuelOpen(true); fetchNextSerial(); }} style={{ borderRadius: 6, borderColor: '#fa541c', color: '#fa541c' }}>
                  New Fuel Name
                </Button>
              )}
              style={{ borderRadius: 12, border: '1px solid #e8ecf1', marginBottom: 16 }}
              styles={{ body: { padding: 12 } }}
            >
              {pdfGroups.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text style={{ color: '#8dabc4', fontSize: 13 }}>Select photos and create PDF groups</Text>} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {pdfGroups.map((group, i) => (
                    <div key={group.id} style={{ border: '1px solid #e8ecf1', borderRadius: 10, padding: '12px 14px', background: '#fafbfd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
                          <Input value={group.label} onChange={e => setPdfGroups(prev => prev.map(g => g.id === group.id ? { ...g, label: e.target.value } : g))} size="small" style={{ width: 150, borderRadius: 6, fontWeight: 600 }} />
                        </div>
                        <Button type="text" size="small" danger icon={<DeleteOutlined style={{ fontSize: 12 }} />} onClick={() => removeGroup(group.id)} />
                      </div>

                      {/* Photo thumbnails */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {group.photo_ids.map((pid, j) => {
                          const photo = photos.find(p => p.photo_id === pid);
                          return photo ? (
                            <img key={pid} src={`${API_BASE_URL}${photo.preview_url}`} alt={`Page ${j + 1}`}
                              style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, border: '1px solid #e8ecf1' }} />
                          ) : null;
                        })}
                      </div>

                      <Text style={{ fontSize: 11, color: '#8dabc4', display: 'block' }}>
                        {group.photo_ids.length} page{group.photo_ids.length > 1 ? 's' : ''}
                      </Text>

                      {/* ═══ FUEL NAME SELECTOR FOR EACH GROUP ═══ */}
                      {isFuelMode && (
                        <div style={{ marginTop: 8, padding: '8px 10px', background: group.fuel_serial ? '#fff2e8' : '#fffbe6', borderRadius: 8, border: group.fuel_serial ? '1px solid #ffbb96' : '1px solid #ffe58f' }}>
                          <Text style={{ fontSize: 11, fontWeight: 700, color: group.fuel_serial ? '#ad2102' : '#d48806', display: 'block', marginBottom: 6 }}>
                            <FireOutlined style={{ marginRight: 4 }} />
                            {group.fuel_serial ? `#${group.fuel_serial} — ${group.fuel_name}` : 'Select Fuel Name:'}
                          </Text>
                          <Select
                            showSearch
                            placeholder="Search by # or name..."
                            value={group.fuel_serial || undefined}
                            onChange={(val) => assignFuelToGroup(group.id, val)}
                            style={{ width: '100%' }}
                            size="small"
                            filterOption={(input, option) => {
                              const q = input.toLowerCase();
                              return (
                                String(option.value).includes(q) ||
                                (option.label || '').toLowerCase().includes(q)
                              );
                            }}
                            options={fuelNames.map(f => ({
                              value: f.serial_no,
                              label: `#${f.serial_no} — ${f.fuel_name}`,
                            }))}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {pdfGroups.length > 0 && (
              <Button type="primary" size="large" icon={<ThunderboltOutlined />} onClick={processAllGroups}
                disabled={ungroupedPhotos.length > 0 || (isFuelMode && pdfGroups.some(g => !g.fuel_serial))}
                style={{
                  width: '100%', height: 52, borderRadius: 12, fontWeight: 600, fontSize: 15,
                  background: ungroupedPhotos.length > 0 || (isFuelMode && pdfGroups.some(g => !g.fuel_serial))
                    ? undefined
                    : isFuelMode ? 'linear-gradient(135deg, #ad2102, #fa541c)' : 'linear-gradient(135deg, #237804, #52c41a)',
                  border: 'none',
                }}>
                {ungroupedPhotos.length > 0
                  ? `Group remaining ${ungroupedPhotos.length} photos first`
                  : isFuelMode && pdfGroups.some(g => !g.fuel_serial)
                    ? 'Assign fuel names to all groups first'
                    : `Process All ${pdfGroups.length} PDFs`
                }
              </Button>
            )}
          </Col>
        </Row>

        {/* Add New Fuel Name Modal */}
        <Modal
          open={addNewFuelOpen}
          onCancel={() => setAddNewFuelOpen(false)}
          title={<><PlusOutlined /> Create New Fuel Name (Serial #{nextSerial})</>}
          footer={null}
          width={400}
        >
          <div style={{ marginTop: 16 }}>
            <Input
              placeholder="e.g., Fuel NewDriver_12345"
              value={newFuelName}
              onChange={(e) => setNewFuelName(e.target.value)}
              onPressEnter={handleAddNewFuel}
              size="large"
              style={{ borderRadius: 8, marginBottom: 12 }}
              autoFocus
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNewFuel} loading={addingFuel} block size="large"
              style={{ borderRadius: 8, background: '#722ed1', borderColor: '#722ed1' }}>
              Create #{nextSerial}
            </Button>
          </div>
        </Modal>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  //  RENDER: PROCESSING
  // ═══════════════════════════════════════════

  if (stage === 'processing') {
    return (
      <div style={{ maxWidth: 500, margin: '40px auto', textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: 20, margin: '0 auto 24px', background: 'linear-gradient(135deg, #e8f1ff, #d6e4f0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <RobotOutlined style={{ fontSize: 36, color: '#1a6ed8' }} />
        </div>
        <Title level={4} style={{ color: '#1a3353', marginBottom: 8 }}>Processing Documents...</Title>
        <Text style={{ color: '#5a6d80', fontSize: 14 }}>Creating PDFs → AI extraction → Renaming → Saving to S3</Text>
        <div style={{ marginTop: 24 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text style={{ color: '#8dabc4', fontSize: 13 }}>Processing {pdfGroups.length} document{pdfGroups.length > 1 ? 's' : ''}...</Text>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  //  RENDER: DONE
  // ═══════════════════════════════════════════

  if (stage === 'done' && processResults) {
    const results = processResults.results || [];
    return (
      <div>
        <div style={{
          background: processResults.success ? 'linear-gradient(135deg, #f6ffed, #eeffdb)' : 'linear-gradient(135deg, #fff7e6, #fff1cc)',
          border: `1px solid ${processResults.success ? '#b7eb8f' : '#ffe58f'}`,
          borderRadius: 12, padding: '24px 28px', marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        }}>
          <CheckCircleFilled style={{ fontSize: 40, color: '#52c41a' }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <Title level={4} style={{ margin: 0, color: '#237804' }}>Scan & Process Complete!</Title>
            <Text style={{ color: '#389e0d', fontSize: 14 }}>
              {processResults.processed}/{processResults.total} documents · {processResults.invoices_detected} invoices
            </Text>
          </div>
        </div>

        <Card style={{ borderRadius: 12, border: '1px solid #e8ecf1', marginBottom: 24 }} styles={{ body: { padding: 0 } }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf1' }}>
            <Text strong style={{ color: '#1a3353', fontSize: 14 }}>Results</Text>
          </div>
          {results.map((r, i) => (
            <div key={i} style={{ padding: '14px 20px', borderBottom: i < results.length - 1 ? '1px solid #f0f3f6' : 'none', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {r.status === 'success' ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 18 }} />}
              <div style={{ flex: 1, minWidth: 200 }}>
                <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block' }}>{r.new_filename || r.label}</Text>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  {r.invoice_number && <Tag icon={<TagOutlined />} color="blue" style={{ borderRadius: 4, fontSize: 10 }}>{r.invoice_number}</Tag>}
                  {r.supplier_name && <Tag icon={<ShopOutlined />} color="purple" style={{ borderRadius: 4, fontSize: 10 }}>{r.supplier_name}</Tag>}
                  {r.total_amount && <Tag color="green" style={{ borderRadius: 4, fontSize: 10 }}>{r.total_amount} AED</Tag>}
                  {r.fuel_serial && <Tag icon={<FireOutlined />} color="volcano" style={{ borderRadius: 4, fontSize: 10 }}>#{r.fuel_serial}</Tag>}
                  {r.needs_review && <Tag icon={<WarningOutlined />} color="warning" style={{ borderRadius: 4, fontSize: 10 }}>Review</Tag>}
                </div>
              </div>
            </div>
          ))}
        </Card>

        <div style={{ display: 'flex', gap: 12 }}>
          <Button type="primary" size="large" icon={<ScanOutlined />}
            onClick={() => { setStage('setup'); setPdfGroups([]); setPhotos([]); setSelectedPhotos(new Set()); setProcessResults(null); setSessionId(null); setFuelAssignments({}); }}
            style={{ borderRadius: 12, height: 48, fontWeight: 600, flex: 1, background: 'linear-gradient(135deg, #1a6ed8, #4a9eff)', border: 'none' }}>
            Scan More
          </Button>
          <Button size="large" onClick={endSession} style={{ borderRadius: 12, height: 48, fontWeight: 600 }}>Done</Button>
        </div>
      </div>
    );
  }

  return null;
};

export default ScanSession;